import { z } from "zod";

/**
 * Validates Canadian Social Insurance Number (SIN) using the Luhn algorithm
 */
const validateSinWithLuhn = (sin: string): boolean => {
  // Remove dashes and spaces
  const strippedSin = sin.replace(/[-\s]/g, "");

  // Must be exactly 9 digits
  if (!/^\d{9}$/.test(strippedSin)) {
    return false;
  }

  // First digit cannot be 0 or 8 (8 is used for temporary SINs being phased out)
  const firstDigit = parseInt(strippedSin[0], 10);
  if (firstDigit === 0 || firstDigit === 8) {
    return false;
  }

  // Reject all zeros or all same digits
  if (/^0{9}$/.test(strippedSin) || /^(\d)\1{8}$/.test(strippedSin)) {
    return false;
  }

  // Apply Luhn algorithm (modulus 10)
  let sum = 0;
  for (let i = 0; i < 9; i += 1) {
    let digit = parseInt(strippedSin[i], 10);

    // Double every second digit (positions 1, 3, 5, 7)
    if (i % 2 === 1) {
      digit *= 2;
      // If doubling results in a two-digit number, add the digits together
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
  }

  // Valid if sum is divisible by 10
  return sum % 10 === 0;
};

export const sin = (errmsg?: string) =>
  z
    .string()
    .trim()
    .refine((val) => {
      // Accept formats: 123-456-789 or 123456789
      const formatRegex = /^(\d{3}-\d{3}-\d{3}|\d{9})$/;
      if (!formatRegex.test(val)) {
        return false;
      }

      return validateSinWithLuhn(val);
    }, errmsg || "Invalid SIN")
    .transform((val) => val.replace(/-/g, ""));
